import React, { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { useAuth } from "../../global-components/src";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { api } from "../../services/api";

export type MediaMsg = {
    id: number;
    text?: string;
    file_path?: string;
    file_type?: string;
    file_name?: string;
    gif_url?: string;
    created_at: string;
    user?: { id: number; name: string; user_name: string; img_account: string };
};

export type MediaTab = "media" | "links" | "docs";

// Shared state/logic for the "Mídia, links e docs" and "Pesquisar mensagens" sub-panels
// used by both the group info panel and the direct chat menu.
export function useChatMediaSearch(chatId?: number | null) {
    const { token } = useAuth();

    // ── Media / Links / Docs ────────────────────────────────────────────────
    const [showMedia, setShowMedia] = useState(false);
    const [mediaTab, setMediaTab] = useState<MediaTab>("media");
    const [mediaItems, setMediaItems] = useState<MediaMsg[]>([]);
    const [mediaCursor, setMediaCursor] = useState<number | null>(null);
    const [mediaHasMore, setMediaHasMore] = useState(false);
    const [loadingMedia, setLoadingMedia] = useState(false);

    const loadMedia = async (tab: MediaTab, cursor?: number | null, reset = false) => {
        if (!chatId || loadingMedia) return;
        setLoadingMedia(true);
        try {
            const cursorParam = cursor ? `&cursor=${cursor}` : "";
            const res = await api(token).get(
                `${PACOCA_API_URL}/chats/messages/${chatId}/media?type=${tab}&limit=30${cursorParam}`
            );
            const items: MediaMsg[] = res.data.messages ?? [];
            setMediaItems(prev => reset ? items : [...prev, ...items]);
            setMediaCursor(res.data.next_cursor ?? null);
            setMediaHasMore(!!res.data.has_more);
        } catch {
            // silent
        } finally {
            setLoadingMedia(false);
        }
    };

    const openMedia = () => {
        setShowMedia(true);
        setMediaTab("media");
        setMediaItems([]);
        setMediaCursor(null);
        loadMedia("media", null, true);
    };

    const switchMediaTab = (tab: MediaTab) => {
        setMediaTab(tab);
        setMediaItems([]);
        setMediaCursor(null);
        loadMedia(tab, null, true);
    };

    // ── Search messages ─────────────────────────────────────────────────────
    const [showMsgSearch, setShowMsgSearch] = useState(false);
    const [msgSearchQuery, setMsgSearchQuery] = useState("");
    const [msgSearchDebounced, setMsgSearchDebounced] = useState("");
    const [msgResults, setMsgResults] = useState<MediaMsg[]>([]);
    const [loadingMsgSearch, setLoadingMsgSearch] = useState(false);

    const debouncedSetMsgSearch = useCallback(debounce((v: string) => setMsgSearchDebounced(v), 400), []);

    const searchMessages = async (q: string) => {
        if (!chatId || !q.trim()) { setMsgResults([]); return; }
        setLoadingMsgSearch(true);
        try {
            const res = await api(token).get(
                `${PACOCA_API_URL}/chats/messages/${chatId}/search-in-chat?q=${encodeURIComponent(q)}&limit=30`
            );
            setMsgResults(res.data.messages ?? []);
        } catch {
            // silent
        } finally {
            setLoadingMsgSearch(false);
        }
    };

    useEffect(() => {
        if (showMsgSearch) searchMessages(msgSearchDebounced);
    }, [msgSearchDebounced]);

    const openMsgSearch = () => {
        setShowMsgSearch(true);
        setMsgSearchQuery("");
        setMsgSearchDebounced("");
        setMsgResults([]);
    };

    // ── Helpers ─────────────────────────────────────────────────────────────
    const parsePaths = (raw?: string): string[] => {
        if (!raw) return [];
        try { return JSON.parse(raw); } catch { return [raw]; }
    };
    const parseTypes = (raw?: string): string[] => {
        if (!raw) return [];
        try { return JSON.parse(raw); } catch { return [raw]; }
    };
    const extractUrl = (text?: string): string | null => {
        if (!text) return null;
        const m = text.match(/https?:\/\/[^\s]+/);
        return m ? m[0] : null;
    };
    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    };
    const highlightText = (text: string, q: string): React.ReactNode => {
        if (!q) return text;
        const idx = text.toLowerCase().indexOf(q.toLowerCase());
        if (idx === -1) return text;
        return (
            <>
                {text.slice(0, idx)}
                <mark style={{ background: "#ffe082", padding: 0 }}>{text.slice(idx, idx + q.length)}</mark>
                {text.slice(idx + q.length)}
            </>
        );
    };

    return {
        showMedia, setShowMedia, mediaTab, mediaItems, mediaCursor, mediaHasMore, loadingMedia,
        loadMedia, openMedia, switchMediaTab,
        showMsgSearch, setShowMsgSearch, msgSearchQuery, setMsgSearchQuery, msgResults, loadingMsgSearch,
        debouncedSetMsgSearch, openMsgSearch,
        parsePaths, parseTypes, extractUrl, formatDate, highlightText,
    };
}
